// ============================================================================
// /subcontractors/:providerId — Subcontractor DETAIL PAGE (2026-07-10 REV 2).
// A full routed page (no drawer/popup), modeled on the facility detail page and
// reached from the Subcontractors CARD GRID landing. Header carries the go-by,
// legal, capability tags, status, owner, location, active count and a
// "Does: <raw service_type list>" line. Content is organised into tabs:
//   Info        — contact, owner, compliance (W9/COI + expiry), documents,
//                 Missing chips [W9, COI, Phone, Payment info, Active assignment].
//   Finances    — revenue/margin tiles, per-facility rate table, feedback.
//   Active Work — active assignments + recent/upcoming project work.
//   Historic    — ended assignments + project work older than 60 days.
// Edit is a SECONDARY action → the existing edit slide-over (with the merged
// Documents & Compliance section, Task 1). Finance-flavoured reads degrade to
// empty on RLS denial.
// ============================================================================
import { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Building2, ChevronDown, ChevronUp, Mail, MessageSquare, Pencil, Phone, Repeat } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { fmtMoney, fmtPct, useSubDetail } from './hooks/use-sub-data';
import { useSubDetailExtras } from './hooks/use-sub-detail-extras';
import type { Capability } from './capabilities';
import { usePartnerDocuments } from '../hr-onboarding/hooks/use-partner-documents';
import EditSubSlideOver from '../hr-onboarding/edit-sub-slideover';
import PartnerDocumentsSection from '../hr-onboarding/partner-documents-section';
import { DeactivateSubModal } from '../hr-onboarding/deactivate-sub-modal';
import { ProfileActions } from './hub/profile-actions';
import { subStatusLabel } from './stages';
import { scoringFailureText, useSubApplication, type ScoringState, type SubApplication } from './hooks/use-sub-application';
import { answeredInLabel, applicationAnswers, fitBand, fitSummary, type AnswerRow } from '../sourcing-intake/answers';
import { fmtDate as fmtTimestamp } from '../../shared/format';
import { COI_LIGHT_CLASS, COI_LIGHT_LABEL, coiTrafficLight, fmtDate } from '../hr-onboarding/utils';

type TabKey = 'info' | 'finances' | 'active' | 'historic';

// Payment info present == onboarding `direct_deposit` checklist item complete
// (link-only; no bank/PII in the DB, per HR PII policy). Mirror of the drawer.
function directDepositComplete(raw: Record<string, unknown> | null): boolean {
  if (!raw) return false;
  const docs = (raw as { documents?: unknown }).documents;
  if (!Array.isArray(docs)) return false;
  return docs.some(
    (it) =>
      it &&
      typeof it === 'object' &&
      (it as { key?: string }).key === 'direct_deposit' &&
      (it as { status?: string }).status === 'complete',
  );
}

export default function SubDetail() {
  const { providerId } = useParams<{ providerId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canWrite = !!profile; // all full employees may edit (Bryan, 2026-07-06)
  // Any employee may mark a sub inactive (Ben, 2026-10-03, item #683 D4; TCSD-0170) —
  // the dependency warning in the modal still lists every building first.
  const canDeactivate = !!profile;
  const [editOpen, setEditOpen] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const { provider, scorecard, facilities, feedback, facilityNames, loading, error, refetch } =
    useSubDetail(providerId);
  const extras = useSubDetailExtras(providerId);
  const { documents } = usePartnerDocuments(providerId ?? null);
  const application = useSubApplication(providerId);
  // The pipeline card's "Read answers" arrives with the full application open.
  const location = useLocation();
  const openApplication = (location.state as { open?: string } | null)?.open === 'application';

  if (error) return <ErrorState body={error} onRetry={refetch} />;
  if (loading) {
    return (
      <div className="mx-auto max-w-5xl space-y-3 p-4 md:p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!provider) {
    return (
      <div className="mx-auto max-w-5xl p-4 md:p-6">
        <EmptyState title="Sub not found" body={`No subcontractor with id ${providerId ?? '—'}.`} />
      </div>
    );
  }

  const sc = scorecard;
  const statusTone =
    provider.provider_status === 'Active'
      ? 'bg-st-ok-bg text-st-ok-fg border-st-ok-border'
      : 'bg-st-warn-bg text-st-warn-fg-strong border-st-warn-border';

  // Compliance from partner_documents (canonical). On file = has a drive_link
  // and is not expired.
  const today = new Date().toISOString().slice(0, 10);
  const hasW9 = documents.some(
    (d) => d.document_type === 'W9' && d.drive_link && (!d.expiration_date || d.expiration_date >= today),
  );
  const coiDoc = documents.find((d) => d.document_type === 'COI');
  const hasCurrentCOI = documents.some(
    (d) => d.document_type === 'COI' && (!d.expiration_date || d.expiration_date >= today),
  );
  const phone = provider.phone || provider.primary_poc_phone || null;
  const paymentOk = directDepositComplete(extras.checklistJson);
  const hasAssignment = facilities.length > 0;

  const missing: string[] = [];
  if (!hasW9) missing.push('W9');
  if (!hasCurrentCOI) missing.push('COI');
  if (!phone) missing.push('Phone');
  if (!paymentOk) missing.push('Payment info');
  if (!hasAssignment) missing.push('Active assignment');

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      {/* Breadcrumb — go back to wherever we came from (card grid or directory) */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-tcs-text-secondary hover:text-tcs-text"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <header className="rounded-lg border border-tcs-border bg-tcs-raised p-4 md:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-tcs-text">{provider.partner_name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-tcs-text-secondary">
              <span className="font-mono text-xs">{provider.provider_id}</span>
              {(provider.coverage_markets ?? []).length > 0 && <span>· {(provider.coverage_markets ?? []).join(' · ')}</span>}
              {provider.legal_business_name && provider.legal_business_name !== provider.partner_name && (
                <span className="text-tcs-text-muted">· {provider.legal_business_name}</span>
              )}
              {provider.owner_name && <span className="text-tcs-text-muted">· {provider.owner_name}</span>}
            </div>
            {/* Capability tags — derived from service_type (Task 2). */}
            {extras.capabilities.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {extras.capabilities.map((c) => (
                  <CapabilityTag key={c} capability={c} />
                ))}
              </div>
            )}
            {/* Raw service_type detail line. */}
            {extras.rawServiceTypes.length > 0 && (
              <p className="mt-2 text-xs text-tcs-text-muted">
                Does: {extras.rawServiceTypes.join(', ')}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {provider.provider_status && (
              <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusTone}`}>
                {subStatusLabel(provider.provider_status)}
              </span>
            )}
            <span className="rounded-full border border-tcs-border bg-tcs-surface px-2.5 py-0.5 text-xs text-tcs-text-secondary tabular-nums">
              {facilities.length} active
            </span>
            <ComplianceChip label="W9" ok={hasW9} />
            <ComplianceChip label="COI" ok={hasCurrentCOI} />
            {canWrite && (
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
          </div>
        </div>
      </header>

      <ProfileActions
        providerId={provider.provider_id}
        name={provider.partner_name}
        phone={phone}
        onChanged={refetch}
      />

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-tcs-border">
        <TabButton label="Info" active={tab === 'info'} onClick={() => setTab('info')} />
        <TabButton label="Finances" active={tab === 'finances'} onClick={() => setTab('finances')} />
        <TabButton label="Active Work" active={tab === 'active'} onClick={() => setTab('active')} />
        <TabButton label="Historic" active={tab === 'historic'} onClick={() => setTab('historic')} />
      </div>

      {/* ---- Info tab ---- */}
      {tab === 'info' && (
        <div className="space-y-5">
          {/* Missing chips */}
          <section className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
            <SectionLabel>Missing</SectionLabel>
            {missing.length === 0 ? (
              <span className="inline-flex rounded-full border border-st-ok-border bg-st-ok-bg px-2.5 py-1 text-xs font-medium text-st-ok-fg">
                Nothing missing
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {missing.map((m) => (
                  <span key={m} className="inline-flex rounded-full border border-st-danger-border bg-st-danger-bg px-2.5 py-1 text-xs font-medium text-st-danger-fg">
                    {m}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Contact + POCs */}
          <section className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
            <SectionLabel>Contact</SectionLabel>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="space-y-1.5">
                <ContactLine icon={<Mail className="h-3.5 w-3.5" />} value={provider.email} href={provider.email ? `mailto:${provider.email}` : undefined} />
                <ContactLine icon={<Phone className="h-3.5 w-3.5" />} value={phone} href={phone ? `tel:${phone}` : undefined} />
                {provider.owner_name && <div className="text-xs text-tcs-text-muted">Owner: {provider.owner_name}</div>}
              </div>
              <PocBlock
                heading="Primary contact"
                name={provider.primary_poc_name ?? provider.owner_name}
                phone={provider.primary_poc_phone}
                email={provider.primary_poc_email}
              />
              {(provider.secondary_poc_name || provider.secondary_poc_phone || provider.secondary_poc_email) && (
                <PocBlock
                  heading="Secondary contact"
                  name={provider.secondary_poc_name}
                  phone={provider.secondary_poc_phone}
                  email={provider.secondary_poc_email}
                />
              )}
            </div>
          </section>

          {/* Application answers (item 833) — only once the application is back. */}
          {application.app && (
            <ApplicationSection app={application.app} scoring={application.scoring} onRescore={application.rescore} startOpen={openApplication} />
          )}
          {application.failed && (
            <section className="rounded-lg border border-tcs-border bg-tcs-raised p-4" aria-label="Application">
              <SectionLabel>Application</SectionLabel>
              <ErrorState title="Couldn't load the application answers" body="Check your connection and try again." onRetry={application.retry} />
            </section>
          )}

          {/* Compliance + documents */}
          <section className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
            <SectionLabel>Compliance &amp; documents</SectionLabel>
            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
              <ComplianceChip label="W9" ok={hasW9} />
              <ComplianceChip label="COI" ok={hasCurrentCOI} />
              {coiDoc?.expiration_date && (
                <span className="inline-flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${COI_LIGHT_CLASS[coiTrafficLight(coiDoc.expiration_date)]}`} />
                  <span className="text-tcs-text-muted">
                    {COI_LIGHT_LABEL[coiTrafficLight(coiDoc.expiration_date)]} · expires {fmtDate(coiDoc.expiration_date)}
                  </span>
                </span>
              )}
            </div>
            {/* AFB-0072 (Antonela, closed 2026-08-07).
                This used to be a READ-ONLY list whose empty state literally said
                "Add them via Edit → Documents & Compliance" — but that editor
                lives inside the hr-onboarding slide-over, which is the ONBOARDING
                surface. Once a provider's partner_onboarding record is Complete
                there was no route back to it, so there was no way to add a
                document to an already-active sub. That is the case Antonela hit.

                Fixed by mounting the SAME self-saving PartnerDocumentsSection
                here, on the service-provider record itself, where it is reachable
                regardless of onboarding status. It writes partner_documents
                (canonical since 2026-07-10) and carries its own role gate, so
                this adds a route, not a permission. */}
            <PartnerDocumentsSection
              providerId={providerId ?? null}
              onboardingId={extras.onboardingId}
              title="Documents"
            />
          </section>
        </div>
      )}

      {/* ---- Finances tab ---- */}
      {tab === 'finances' && (
        <div className="space-y-5">
          {sc && (
            <section className="space-y-2">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <Tile label="Active facilities" value={String(sc.active_facilities)} />
                <Tile label="Client billing /mo" value={fmtMoney(sc.monthly_client_billing)} />
                <Tile label="Sub cost /mo" value={fmtMoney(sc.monthly_sub_cost)} />
                <Tile label="Gross margin /mo" value={fmtMoney(sc.monthly_gross_margin)} />
                <Tile
                  label="Margin %"
                  value={sc.flat_facilities > 0 ? fmtPct(sc.margin_pct) : '—'}
                  warn={sc.flat_facilities > 0 && sc.margin_pct != null && Number(sc.margin_pct) < 10}
                />
              </div>
              {sc.variable_facilities > 0 && (
                <p className="text-xs text-tcs-text-muted">
                  Billing, cost &amp; margin cover flat-billed work only ({sc.flat_facilities}{' '}
                  {sc.flat_facilities === 1 ? 'facility' : 'facilities'}). {sc.variable_facilities} hourly/variable{' '}
                  {sc.variable_facilities === 1 ? 'facility' : 'facilities'} (recorded sub cost{' '}
                  {fmtMoney(sc.variable_sub_cost)}/mo) {sc.variable_facilities === 1 ? 'is' : 'are'} excluded —
                  hourly billing lives in invoicing, not facility monthly rates.
                </p>
              )}
            </section>
          )}

          {/* Per-facility rate table */}
          <section className="rounded-lg border border-tcs-border bg-tcs-raised">
            <div className="border-b border-tcs-border px-4 py-3">
              <h2 className="text-sm font-semibold text-tcs-text">Per-facility rates ({facilities.length})</h2>
            </div>
            {facilities.length === 0 ? (
              <p className="px-4 py-6 text-sm text-tcs-text-muted">No active facility assignments.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-tcs-border text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                      <th className="px-4 py-2">Facility</th>
                      <th className="px-3 py-2">Role</th>
                      <th className="px-3 py-2 text-right">Billing /mo</th>
                      <th className="px-3 py-2 text-right">Contract rate /mo</th>
                      <th className="px-3 py-2 text-right">Margin /mo</th>
                      <th className="px-3 py-2">AM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-tcs-border">
                    {facilities.map((f) => (
                      <tr
                        key={f.assignment_id}
                        onClick={() => navigate(`/account-management/facilities/${f.facility_id}`)}
                        className="cursor-pointer hover:bg-tcs-surface"
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium text-tcs-text">{f.facility_name}</div>
                          <div className="text-xs text-tcs-text-muted">
                            <span className="font-mono">{f.facility_id}</span>
                            {f.market_region && <span> · {f.market_region}</span>}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-tcs-text-secondary">{f.service_label ?? '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {f.client_billing_monthly == null && f.billing_method
                            ? <span className="text-xs text-tcs-text-muted">{f.billing_method}</span>
                            : fmtMoney(f.client_billing_monthly)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(f.sub_rate_monthly)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(f.gross_margin_monthly)}</td>
                        <td className="px-3 py-2 text-xs text-tcs-text-secondary">{f.account_manager_eid ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Feedback (time-accurate) */}
          <section className="rounded-lg border border-tcs-border bg-tcs-raised">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-tcs-border px-4 py-3">
              <h2 className="text-sm font-semibold text-tcs-text">Feedback</h2>
              {sc && (
                <div className="flex flex-wrap items-center gap-2 text-xs text-tcs-text-secondary">
                  <span>{sc.feedback_total} total</span>
                  <span className="rounded bg-st-danger-bg px-1.5 py-0.5 text-st-danger-fg">{sc.negative_fb} negative</span>
                  <span className="rounded bg-st-warn-bg px-1.5 py-0.5 text-st-warn-fg-strong">{sc.open_fb} open</span>
                  <span className="text-tcs-text-muted">({sc.fb_precise} precise · {sc.fb_baseline} baseline)</span>
                </div>
              )}
            </div>
            {feedback.length === 0 ? (
              <p className="px-4 py-6 text-sm text-tcs-text-muted">No feedback attributed to this sub.</p>
            ) : (
              <ul className="divide-y divide-tcs-border">
                {feedback.map((fb) => (
                  <li key={`${fb.feedback_id}-${fb.facility_id}`} className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <SentimentPill sentiment={fb.sentiment} />
                      <Link to={`/account-management/facilities/${fb.facility_id}`} className="font-medium text-tcs-text hover:underline">
                        {facilityNames[fb.facility_id] ?? fb.facility_id}
                      </Link>
                      <span className="text-xs text-tcs-text-muted">
                        {fb.category ?? 'Uncategorized'} · {fb.feedback_date}
                        {fb.status && ` · ${fb.status}`}
                      </span>
                      <AttributionBadge quality={fb.attribution_quality} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {/* ---- Active Work tab ---- */}
      {tab === 'active' && (
        <div className="space-y-5">
          <section className="rounded-lg border border-tcs-border bg-tcs-raised">
            <div className="border-b border-tcs-border px-4 py-3">
              <h2 className="text-sm font-semibold text-tcs-text">Active assignments ({facilities.length})</h2>
            </div>
            {facilities.length === 0 ? (
              <p className="px-4 py-6 text-sm text-tcs-text-muted">No active facility assignments.</p>
            ) : (
              <ul className="divide-y divide-tcs-border">
                {facilities.map((f) => (
                  <li
                    key={f.assignment_id}
                    onClick={() => navigate(`/account-management/facilities/${f.facility_id}`)}
                    className="flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 hover:bg-tcs-surface"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-medium text-tcs-text">{f.facility_name}</div>
                      <div className="text-xs text-tcs-text-muted">
                        {f.service_label ?? 'Assignment'}{f.market_region ? ` · ${f.market_region}` : ''}
                      </div>
                    </div>
                    <span className="shrink-0 text-xs text-tcs-text-secondary tabular-nums">{fmtMoney(f.sub_rate_monthly)}/mo</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <ProjectWorkSection
            title="Recent &amp; upcoming project work"
            emptyLabel="No recent or upcoming project work (last 60 days / upcoming)."
            items={extras.recentProjects}
            loading={extras.loading}
          />
        </div>
      )}

      {/* ---- Historic tab ---- */}
      {tab === 'historic' && (
        <div className="space-y-5">
          <section className="rounded-lg border border-tcs-border bg-tcs-raised">
            <div className="border-b border-tcs-border px-4 py-3">
              <h2 className="text-sm font-semibold text-tcs-text">Ended assignments ({extras.endedAssignments.length})</h2>
            </div>
            {extras.loading ? (
              <div className="p-4"><Skeleton className="h-8 w-full" /></div>
            ) : extras.endedAssignments.length === 0 ? (
              <p className="px-4 py-6 text-sm text-tcs-text-muted">No ended assignments on file.</p>
            ) : (
              <ul className="divide-y divide-tcs-border">
                {extras.endedAssignments.map((a) => (
                  <li key={a.assignment_id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate font-medium text-tcs-text">{a.facility_name}</div>
                      <div className="text-xs text-tcs-text-muted">{a.service_label ?? '—'}</div>
                    </div>
                    <span className="shrink-0 text-[11px] text-tcs-text-muted">{a.status ?? '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <ProjectWorkSection
            title="Past project work"
            emptyLabel="No project work older than 60 days."
            items={extras.historicProjects}
            loading={extras.loading}
          />
        </div>
      )}

      {/* Actions — reuse existing flows, no new write paths */}
      <section className="flex flex-wrap items-center gap-2 pt-2">
        <Link to="/sub-replacement/new" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm text-tcs-text hover:bg-tcs-surface">
          <Repeat className="h-3.5 w-3.5" /> Change sub for a facility
        </Link>
        <Link to="/project-work" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm text-tcs-text hover:bg-tcs-surface">
          <MessageSquare className="h-3.5 w-3.5" /> Projects & Billing Comms
        </Link>
        <Link to="/subcontractors" className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm text-tcs-text hover:bg-tcs-surface">
          <Building2 className="h-3.5 w-3.5" /> All subs
        </Link>
      </section>

      {editOpen && (
        <EditSubSlideOver
          providerId={provider.provider_id}
          onboardingId={extras.onboardingId}
          displayName={provider.partner_name}
          providerStatus={provider.provider_status}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            refetch();
            extras.refetch();
          }}
          onDeactivate={
            canDeactivate && provider.provider_status !== 'Inactive'
              ? () => {
                  setEditOpen(false);
                  setDeactivating(true);
                }
              : undefined
          }
        />
      )}
      {deactivating && (
        <DeactivateSubModal
          providerId={provider.provider_id}
          providerName={provider.partner_name}
          onClose={() => setDeactivating(false)}
          onDone={refetch}
        />
      )}
    </div>
  );
}

// ----- Presentational helpers ----------------------------------------------

function TabButton({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
        active
          ? 'border-tcs-text text-tcs-text'
          : 'border-transparent text-tcs-text-secondary hover:text-tcs-text'
      }`}
    >
      {label}
    </button>
  );
}

const CAPABILITY_TONE: Record<Capability, string> = {
  Cleaning: 'bg-st-info-bg text-st-info-fg border-st-info-border',
  Floor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Carpet: 'bg-violet-50 text-violet-700 border-violet-200',
  Windows: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  Grounds: 'bg-st-ok-bg text-st-ok-fg border-st-ok-border',
  'Snow Removal': 'bg-st-info-bg text-st-info-fg border-st-info-border',
  'Day Porter': 'bg-st-warn-bg text-st-warn-fg-strong border-st-warn-border',
  Management: 'bg-tcs-surface text-tcs-text-secondary border-tcs-border',
  Other: 'bg-tcs-surface text-tcs-text-secondary border-tcs-border',
};

function CapabilityTag({ capability }: { capability: Capability }) {
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${CAPABILITY_TONE[capability]}`}>
      {capability}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The sub's application answers (item 833, Ben 2026-10-13, Option 1). Read
// through from the application record by useSubApplication; worded and ordered
// by the form itself (sourcing-intake/answers.ts). The pipeline card's "Read
// answers" lands here by its id, through Home's focusWhenReady.
// Ben, 2026-10-14 (TCSD-0494): the section opens on the AI fit score and its
// summary ONLY; "Read the full application" opens every answer underneath, so
// the application never takes over the profile.
// ---------------------------------------------------------------------------
function ApplicationSection({
  app,
  scoring,
  onRescore,
  startOpen = false,
}: {
  app: SubApplication;
  scoring: ScoringState;
  onRescore: () => void;
  startOpen?: boolean;
}) {
  const [open, setOpen] = useState(startOpen);
  const a = applicationAnswers(app.intakeJson);
  const lang = answeredInLabel(app.language);
  const head = [
    app.submittedAt ? `Submitted ${fmtTimestamp(app.submittedAt)}` : 'Submitted',
    lang ? `answered in ${lang}` : null,
  ].filter(Boolean).join(' · ');
  return (
    <section id="application" className="scroll-mt-24 rounded-lg border border-tcs-border bg-tcs-raised p-4" aria-label="Application">
      <SectionLabel>Application</SectionLabel>
      <p className="text-sm text-tcs-text-secondary">
        {head}
        {a.countsAgainst > 0 && (
          <>
            {' · '}
            <span className="font-medium text-st-danger-fg">
              {a.countsAgainst} answer{a.countsAgainst === 1 ? '' : 's'} count{a.countsAgainst === 1 ? 's' : ''} against
            </span>
          </>
        )}
      </p>
      <FitScore app={app} scoring={scoring} onRescore={onRescore} />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="mt-3 flex w-full items-center justify-between rounded-lg border border-tcs-border bg-tcs-surface px-3 py-2.5 text-sm font-medium text-tcs-text hover:bg-tcs-bg-muted"
      >
        {open ? 'Hide the full application' : 'Read the full application'}
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {open && (
      <>
      <div className="mt-4">
        <SectionLabel>How they work</SectionLabel>
        <dl className="divide-y divide-tcs-border">
          {a.howTheyWork.map((r) => (
            <AnswerItem key={r.id} row={r} />
          ))}
        </dl>
      </div>
      <div className="mt-4">
        <SectionLabel>In their own words</SectionLabel>
        <dl className="divide-y divide-tcs-border">
          {a.inTheirOwnWords.map((r) => (
            <AnswerItem key={r.id} row={r} long />
          ))}
        </dl>
      </div>
      </>
      )}
    </section>
  );
}

const BAND_CLASS: Record<ReturnType<typeof fitBand>['tone'], string> = {
  ok: 'border-st-ok-border bg-st-ok-bg text-st-ok-fg',
  warn: 'border-st-warn-border bg-st-warn-bg text-st-warn-fg-strong',
  neutral: 'border-tcs-border bg-tcs-surface text-tcs-text-secondary',
  danger: 'border-st-danger-border bg-st-danger-bg text-st-danger-fg',
};

/** The score out of 100, its band, and the AI's summary — or why there isn't one yet. */
function FitScore({ app, scoring, onRescore }: { app: SubApplication; scoring: ScoringState; onRescore: () => void }) {
  if (scoring.kind === 'failed') {
    return (
      <p className="mt-3 text-sm text-st-danger-fg">
        Couldn't score it: {scoringFailureText(scoring.message)}.{' '}
        <button type="button" onClick={onRescore} className="font-medium underline">
          Try again
        </button>
      </p>
    );
  }
  if (app.fitScore === null || scoring.kind === 'running') {
    return <p className="mt-3 text-sm text-tcs-text-muted">Scoring the application…</p>;
  }
  const band = fitBand(app.fitScore);
  const summary = fitSummary(app.fitSummary);
  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-3xl font-semibold leading-none text-tcs-text tabular-nums">{app.fitScore}</span>
        <span className="text-sm text-tcs-text-muted">/ 100</span>
        <span className={`rounded-full border px-2 py-0.5 text-xs font-medium ${BAND_CLASS[band.tone]}`}>{band.label}</span>
        {summary.provisional && (
          <span className="rounded-full border border-tcs-border px-2 py-0.5 text-xs text-tcs-text-muted">Provisional — thin answers</span>
        )}
      </div>
      {summary.text && <p className="mt-2 text-sm text-tcs-text">{summary.text}</p>}
    </div>
  );
}

function AnswerItem({ row, long }: { row: AnswerRow; long?: boolean }) {
  return (
    <div className="py-2">
      <dt className="text-xs text-tcs-text-muted">{row.question}</dt>
      {row.help && <dd className="text-xs text-tcs-text-muted">{row.help}</dd>}
      <dd className={`mt-0.5 text-sm ${row.answered ? 'text-tcs-text' : 'text-tcs-text-muted'}`}>
        {long && row.answered ? <ClampedText text={row.answer} /> : row.answer}
        {row.countsAgainst && (
          <span className="ml-2 inline-flex rounded-full border border-st-danger-border bg-st-danger-bg px-2 py-0.5 align-middle text-[11px] font-medium text-st-danger-fg">
            Counts against
          </span>
        )}
      </dd>
    </div>
  );
}

/** Three lines, then "more" opens the rest in place — shown only when there is more. */
function ClampedText({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !open) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);
  return (
    <>
      <p ref={ref} className={`whitespace-pre-line ${open ? '' : 'line-clamp-3'}`}>
        {text}
      </p>
      {overflows && !open && (
        <button type="button" onClick={() => setOpen(true)} className="text-xs font-medium text-tcs-text underline">
          more
        </button>
      )}
    </>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">{children}</div>;
}

function ProjectWorkSection({
  title,
  emptyLabel,
  items,
  loading,
}: {
  title: string;
  emptyLabel: string;
  items: import('./hooks/use-sub-detail-extras').SubProjectWork[];
  loading: boolean;
}) {
  return (
    <section className="rounded-lg border border-tcs-border bg-tcs-raised">
      <div className="border-b border-tcs-border px-4 py-3">
        <h2 className="text-sm font-semibold text-tcs-text">{title} ({items.length})</h2>
      </div>
      {loading ? (
        <div className="p-4"><Skeleton className="h-8 w-full" /></div>
      ) : items.length === 0 ? (
        <p className="px-4 py-6 text-sm text-tcs-text-muted">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-tcs-border">
          {items.map((p) => (
            <li key={p.key} className="flex items-center justify-between gap-2 px-4 py-2.5">
              <div className="min-w-0">
                <div className="truncate font-medium text-tcs-text">{p.label}</div>
                <div className="text-xs text-tcs-text-muted">
                  {p.status ?? '—'}{p.date ? ` · ${fmtDate(p.date)}` : ''}
                </div>
              </div>
              <span className="shrink-0 text-xs text-tcs-text-secondary tabular-nums">
                {p.amount == null ? '—' : fmtMoney(p.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ComplianceChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        ok ? 'border-st-ok-border bg-st-ok-bg text-st-ok-fg' : 'border-st-danger-border bg-st-danger-bg text-st-danger-fg'
      }`}
    >
      {label} {ok ? 'on file' : 'missing'}
    </span>
  );
}

function ContactLine({ icon, value, href }: { icon: React.ReactNode; value: string | null | undefined; href?: string }) {
  if (!value) return <div className="flex items-center gap-1.5 text-tcs-text-muted">{icon} <span>—</span></div>;
  return (
    <a href={href} className="flex items-center gap-1.5 text-tcs-text hover:text-tcs-accent-ink">
      {icon} <span className="truncate">{value}</span>
    </a>
  );
}

function PocBlock({
  heading,
  name,
  phone,
  email,
}: {
  heading: string;
  name: string | null;
  phone: string | null;
  email: string | null;
}) {
  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg-muted/40 p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">{heading}</div>
      <div className="mt-1 text-sm font-medium text-tcs-text">{name ?? '—'}</div>
      <div className="mt-1 space-y-0.5 text-xs text-tcs-text-secondary">
        {phone && (
          <a href={`tel:${phone}`} className="flex items-center gap-1.5 hover:text-tcs-text">
            <Phone className="h-3 w-3" /> {phone}
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} className="flex items-center gap-1.5 hover:text-tcs-text">
            <Mail className="h-3 w-3" /> {email}
          </a>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-raised p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${warn ? 'text-st-danger-fg' : 'text-tcs-text'}`}>{value}</div>
    </div>
  );
}

function SentimentPill({ sentiment }: { sentiment: string | null }) {
  const tone =
    sentiment === 'Positive'
      ? 'bg-st-ok-bg text-st-ok-fg'
      : sentiment === 'Critical'
        ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
        : sentiment === 'Negative'
          ? 'bg-st-danger-bg text-st-danger-fg'
          : 'bg-tcs-surface text-tcs-text-secondary';
  return <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${tone}`}>{sentiment ?? 'Neutral'}</span>;
}

function AttributionBadge({ quality }: { quality: 'precise' | 'baseline' }) {
  return (
    <span
      title={
        quality === 'precise'
          ? 'Attributed from the assignment that actually covered this date'
          : 'Pre-system history (before 2026-04-13) — attribution is approximate'
      }
      className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
        quality === 'precise' ? 'bg-st-info-bg text-st-info-fg' : 'bg-tcs-surface text-tcs-text-muted'
      }`}
    >
      {quality}
    </span>
  );
}
